import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import INotificationProps from '../dtos/INotificationProps';
import { useAuth } from './auth';
import api from '../services/api';

enum ACTIONS {
  ADD_NOTIFICATIONS = 'ADD_NOTIFICATIONS',
  MARK_AS_READ = 'MARK_AS_READ',
  MARK_ALL_AS_READ = 'MARK_ALL_AS_READ',
  REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION',
  CLEAR_ALL = 'CLEAR_ALL',
  SYNC_UNREAD_COUNT = 'SYNC_UNREAD_COUNT',
}

interface INotificationState {
  notifications: INotificationProps[];
  unreadCount: number;
}

const initialState: INotificationState = {
  notifications: [] as INotificationProps[],
  unreadCount: 0,
};

interface Action {
  type: ACTIONS;
  payload?: unknown;
}

const notificationReducer = (
  state: INotificationState,
  action: Action,
): typeof initialState => {
  switch (action.type) {
    case ACTIONS.ADD_NOTIFICATIONS: {
      const newNotifications = (action.payload as INotificationProps[]).filter(
        (newNotif: INotificationProps) =>
          !state.notifications.some(
            existingNotif => existingNotif.id === newNotif.id,
          ),
      );

      // Contando apenas notificações não lidas
      const newUnreadCount = newNotifications.filter(n => !n.read).length;

      return {
        ...state,
        notifications: [...newNotifications, ...state.notifications],
        unreadCount: state.unreadCount + newUnreadCount,
      };
    }

    case ACTIONS.MARK_AS_READ: {
      // Verificar se a notificação existe e não está lida antes de decrementar
      const notificationExists = state.notifications.some(
        n => n.id === action.payload && !n.read,
      );

      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, read: true }
            : notification,
        ),
        // Só decrementa se a notificação existe e não estava lida
        unreadCount: notificationExists
          ? state.unreadCount - 1
          : state.unreadCount,
      };
    }
    case ACTIONS.MARK_ALL_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(notification => ({
          ...notification,
          read: true,
        })),
        // Reseta o contador para zero já que todas notificações foram lidas
        unreadCount: 0,
      };
    case ACTIONS.REMOVE_NOTIFICATION: {
      const notification = state.notifications.find(
        n => n.id === action.payload,
      );
      const unreadDelta = notification && !notification.read ? 1 : 0;
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        unreadCount: Math.max(0, state.unreadCount - unreadDelta),
      };
    }
    case ACTIONS.SYNC_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload as number,
      };
    case ACTIONS.CLEAR_ALL:
      return initialState;
    default:
      return state;
  }
};

interface INotificationContext {
  notifications: INotificationProps[];
  unreadCount: number;
  addNotifications: (notifications: INotificationProps[]) => void;
  markAsRead: (notificationID: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationID: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<INotificationContext>(
  {} as INotificationContext,
);

const NotificationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { updateUserPoints, user } = useAuth();

  // Chave do localStorage isolada por usuário — evita compartilhamento entre contas
  const storageKey = user?.id ? `notifications_${user.id}` : null;

  // IDs já processados para evitar chamadas duplicadas à API de gamificação
  const processedPointsRef = useRef<Set<string>>(new Set());

  // Atualiza pontos de gamificação ao receber notificações com pontos,
  // mas NÃO marca como lida — isso só acontece quando o usuário abre a notificação
  useEffect(() => {
    const notificationsWithPoints = state.notifications.filter(
      notification =>
        !notification.read &&
        notification.points &&
        notification.points > 0 &&
        !processedPointsRef.current.has(notification.id),
    );

    if (notificationsWithPoints.length === 0) return;

    // Marcar como processados imediatamente para evitar reprocessamento
    notificationsWithPoints.forEach(n => processedPointsRef.current.add(n.id));

    const updateUserGamePoints = async (): Promise<void> => {
      try {
        const response = await api.get('users/update/gamification');
        updateUserPoints(response.data);
        // Não chama MARK_AS_READ aqui — a notificação deve permanecer visível
        // até o usuário a ler manualmente
      } catch {
        // Falha silenciosa — será tentado novamente na próxima atualização
        // Remove do set para permitir nova tentativa
        notificationsWithPoints.forEach(n =>
          processedPointsRef.current.delete(n.id),
        );
      }
    };

    updateUserGamePoints();
  }, [state.notifications, updateUserPoints]);

  // Persiste notificações no localStorage com chave isolada por usuário
  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify(state.notifications));
  }, [state.notifications, storageKey]);

  // Carrega notificações do localStorage ao montar ou ao trocar de usuário
  useEffect(() => {
    if (!storageKey) return;

    // Limpa estado anterior ao trocar de usuário
    dispatch({ type: ACTIONS.CLEAR_ALL });
    processedPointsRef.current = new Set();

    try {
      const savedNotifications = localStorage.getItem(storageKey);

      if (savedNotifications) {
        const parsedNotifications = JSON.parse(savedNotifications);

        if (
          Array.isArray(parsedNotifications) &&
          parsedNotifications.length > 0
        ) {
          dispatch({
            type: ACTIONS.ADD_NOTIFICATIONS,
            payload: parsedNotifications,
          });
        }
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  const addNotifications = useCallback(
    (notifications: INotificationProps[]) => {
      dispatch({ type: ACTIONS.ADD_NOTIFICATIONS, payload: notifications });
    },
    [],
  );
  // não atualize o array de dependências
  const markAsRead = useCallback((notificationID: string) => {
    dispatch({ type: ACTIONS.MARK_AS_READ, payload: notificationID });
  }, []);

  const markAllAsRead = useCallback(() => {
    dispatch({ type: ACTIONS.MARK_ALL_AS_READ });
  }, []);

  const removeNotification = useCallback((notificationID: string) => {
    dispatch({ type: ACTIONS.REMOVE_NOTIFICATION, payload: notificationID });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_ALL });
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        addNotifications,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

function useNotification(): INotificationContext {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }
  return context;
}

export { NotificationProvider, useNotification, ACTIONS };
